import "./settings.css";
import { useContext, useEffect, useState } from "react";
import { Context } from "../../Context/Context";
import { usersApi, uploadApi, settingsApi } from "../../services";
import toast from "../../utils/toast";

export default function Settings() {
  const [file, setFile] = useState(null);
  const [success, setSuccess] = useState(false);

  const { user, dispatch } = useContext(Context);

  // Ô nhập hiển thị sẵn giá trị đang dùng — trước đây chỉ để placeholder nên
  // state luôn rỗng, bấm "Cập nhật" (vd chỉ đổi ảnh) là gửi username/email
  // rỗng lên server. Đổi mật khẩu có form riêng (FormChangePwd) nên trang này
  // không gửi field password nữa.
  const [username, setUsername] = useState(user?.username || "");
  const [email, setEmail] = useState(user?.email || "");

  // ── Cấu hình giảm giá (Settings.discountRules) ──
  const [discountRules, setDiscountRules] = useState([]);
  const [discountLoading, setDiscountLoading] = useState(true);
  const [discountSaving, setDiscountSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await settingsApi.getSettings();
        setDiscountRules(
          Array.isArray(data?.discountRules) ? data.discountRules : []
        );
      } catch (err) {
        toast.error("Không tải được cấu hình giảm giá");
      } finally {
        setDiscountLoading(false);
      }
    })();
  }, []);

  const handleRuleChange = (index, field, value) => {
    setDiscountRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
    );
  };

  const handleAddRule = () => {
    setDiscountRules((prev) => [...prev, { keyword: "", discountPercent: 0 }]);
  };

  const handleRemoveRule = (index) => {
    setDiscountRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveDiscountRules = async () => {
    const cleaned = discountRules
      .map((r) => ({
        keyword: String(r.keyword || "").trim(),
        discountPercent: Number(r.discountPercent),
      }))
      .filter((r) => r.keyword);

    for (const r of cleaned) {
      if (isNaN(r.discountPercent) || r.discountPercent < 0 || r.discountPercent > 100) {
        toast.error(`% giảm giá của từ khóa "${r.keyword}" phải là số từ 0 đến 100`);
        return;
      }
    }

    setDiscountSaving(true);
    try {
      const updated = await settingsApi.updateSettings({ discountRules: cleaned });
      setDiscountRules(updated?.discountRules || cleaned);
      toast.success("Đã lưu cấu hình giảm giá!");
    } catch (err) {
      toast.error("Lưu cấu hình giảm giá thất bại");
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return toast.error("Tên người dùng không được để trống");
    if (!email.trim()) return toast.error("Email không được để trống");

    dispatch({ type: "UPDATE_START" });
    const updatedUser = {
      userId: user._id,
      username: username.trim(),
      email: email.trim(),
    };
    if (file) {
      const tempName = Date.now() + "-" + file.name;
      try {
        const res = await uploadApi.uploadFile(file, tempName);
        updatedUser.profilePic = res?.filename || tempName;
      } catch (err) {}
    }
    try {
      const res = await usersApi.updateUser(user._id, updatedUser);
      setSuccess(true);
      dispatch({ type: "UPDATE_SUCCESS", payload: res });
    } catch (err) {
      dispatch({ type: "UPDATE_FAILURE" });
    }
  };
  return (
    <div className="settings">
      <div className="settingsWrapper">
        <div className="settingsTitle">
          <span className="settingsUpdateTitle">Tài khoản</span>
        </div>
        <form className="settingsForm" onSubmit={handleSubmit}>
          <label>Ảnh đại diện</label>
          <div className="settingsPP">
            <img
              src="https://images.unsplash.com/photo-1517849845537-4d257902454a"
              alt=""
            />
            <label htmlFor="fileInput">
              <i className="settingsPPIcon far fa-user-circle"></i>
            </label>
            <input
              type="file"
              id="fileInput"
              style={{ display: "none" }}
              onChange={(e) => setFile(e.target.files[0])}
            />
          </div>
          <label>Tên người dùng</label>
          <input
            type="text"
            value={username}
            placeholder="Tên đăng nhập admin"
            onChange={(e) => setUsername(e.target.value)}
          />
          <label>Email</label>
          <input
            type="email"
            value={email}
            placeholder="Email admin"
            onChange={(e) => setEmail(e.target.value)}
          />
          {success && (
            <span style={{ color: "var(--color-success)", marginTop: "16px", textAlign: "center", fontSize: "14px" }}>
              Cập nhật thành công!
            </span>
          )}
          <button className="settingsSubmit" type="submit">
            Cập nhật
          </button>
        </form>

        <div className="settingsTitle" style={{ marginTop: "40px" }}>
          <span className="settingsUpdateTitle">Cấu hình giảm giá</span>
        </div>
        <div className="settingsForm">
          <p style={{ color: "var(--text-secondary)", fontSize: "13px", margin: "0 0 8px" }}>
            Khách gõ từ khóa (không phân biệt hoa/thường) vào ô khách thuê bên hệ thống
            thứ 3 → giá phiếu thuê đó được giảm % tương ứng. Khớp nhiều từ khóa cùng lúc
            sẽ áp dụng % lớn nhất.
          </p>

          {discountLoading ? (
            <p style={{ color: "var(--text-tertiary)" }}>Đang tải...</p>
          ) : (
            <>
              {discountRules.length === 0 && (
                <p style={{ color: "var(--text-tertiary)", fontSize: "13px" }}>
                  Chưa có quy tắc giảm giá nào.
                </p>
              )}
              {discountRules.map((rule, index) => (
                <div key={index} className="discountRuleRow">
                  <input
                    type="text"
                    className="discountRuleInput discountRuleKeyword"
                    placeholder="Từ khóa (vd: card)"
                    value={rule.keyword}
                    onChange={(e) => handleRuleChange(index, "keyword", e.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="discountRuleInput discountRulePercent"
                    placeholder="% giảm"
                    value={rule.discountPercent}
                    onChange={(e) => handleRuleChange(index, "discountPercent", e.target.value)}
                  />
                  <span className="discountRulePercentSign">%</span>
                  <i
                    className="fas fa-trash discountRuleRemove"
                    onClick={() => handleRemoveRule(index)}
                  ></i>
                </div>
              ))}

              <button type="button" className="discountRuleAdd" onClick={handleAddRule}>
                + Thêm quy tắc
              </button>

              <button
                type="button"
                className="settingsSubmit"
                disabled={discountSaving}
                onClick={handleSaveDiscountRules}
              >
                {discountSaving ? "Đang lưu..." : "Lưu cấu hình"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
